import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Snackbar,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Grid,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Help as HelpIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  EmojiEvents as LevelIcon,
  Contacts as ContactIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Check as CheckIcon,
  Tune as FeatureIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { API_BASE_URL, getApiBaseUrlWithFallback } from '../config/environment';
import { ADULT_SKATER_LEVELS } from '../utils/skatingLevels';

const ALL_LEVELS = [
  { category: 'Learn to Skate', levels: ['Snowplow Sam', 'Basic 1', 'Basic 2', 'Basic 3', 'Basic 4', 'Basic 5', 'Basic 6'] },
  { category: 'Aspire', levels: ['Aspire 1', 'Aspire 2', 'Aspire 3', 'Aspire 4'] },
  { category: 'Free Skate', levels: ['Pre-Preliminary', 'Preliminary', 'Pre-Juvenile', 'Juvenile', 'Intermediate', 'Novice', 'Junior', 'Senior'] },
  { category: 'Adult', levels: [...ADULT_SKATER_LEVELS] },
];
const ALL_LEVEL_OPTIONS = ALL_LEVELS.flatMap((cat) => cat.levels);

function SectionCard({ title, icon, children, onEdit }) {
  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.50', display: 'flex' }}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          </Box>
          <Tooltip title="Edit">
            <IconButton onClick={onEdit} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function ViewField({ label, value, icon, isLink }) {
  const displayValue = value || '—';
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color: 'action.active' }}>{icon}</Box>}
        {isLink && value ? (
          <Link href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener">
            <Typography variant="body1">{displayValue}</Typography>
          </Link>
        ) : (
          <Typography variant="body1" sx={{ color: value ? 'text.primary' : 'text.secondary' }}>{displayValue}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default function SeriesSettings({ interclubId }) {
  const { getIdToken } = useFirebaseAuth();
  const [interclub, setInterclub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    brandingTitle: '',
    brandingDescription: '',
    logoUrl: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    seasonFeeDescription: '',
    helpTitle: 'Need Help?',
    helpContent: '',
    helpEmail: '',
    supportedLevels: [],
    enableQualifiers: true,
  });

  const loadInterclub = useCallback(async () => {
    if (!interclubId) return;
    try {
      setLoading(true);
      setError(null);
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}`);
      if (!response.ok) throw new Error('Failed to load interclub data');
      const data = await response.json();
      setInterclub(data);
      setFormData({
        brandingTitle: data.brandingTitle || '',
        brandingDescription: data.brandingDescription || '',
        logoUrl: data.logoUrl || '',
        contactEmail: data.contactEmail || data.contact?.email || '',
        contactPhone: data.contactPhone || data.contact?.phone || '',
        website: data.website || data.contact?.website || '',
        seasonFeeDescription: data.seasonFeeDescription || '',
        helpTitle: data.helpTitle || 'Need Help?',
        helpContent: data.helpContent || '',
        helpEmail: data.helpEmail || data.contact?.email || '',
        supportedLevels: data.supportedLevels || ALL_LEVEL_OPTIONS,
        enableQualifiers: data.enableQualifiers !== false,
      });
    } catch (err) {
      console.error('Error loading interclub:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId]);

  useEffect(() => { loadInterclub(); }, [loadInterclub]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleLevelToggle = (level) => {
    setFormData((prev) => {
      const currentLevels = prev.supportedLevels || [];
      if (currentLevels.includes(level)) return { ...prev, supportedLevels: currentLevels.filter((l) => l !== level) };
      return { ...prev, supportedLevels: [...currentLevels, level] };
    });
  };

  const handleCategoryToggle = (categoryLevels) => {
    setFormData((prev) => {
      const currentLevels = prev.supportedLevels || [];
      const allSelected = categoryLevels.every((level) => currentLevels.includes(level));
      if (allSelected) return { ...prev, supportedLevels: currentLevels.filter((l) => !categoryLevels.includes(l)) };
      return { ...prev, supportedLevels: Array.from(new Set([...currentLevels, ...categoryLevels])) };
    });
  };

  const handleSelectAll = () => setFormData((prev) => ({ ...prev, supportedLevels: [...ALL_LEVEL_OPTIONS] }));
  const handleClearAll = () => setFormData((prev) => ({ ...prev, supportedLevels: [] }));

  const handleSave = async (section) => {
    try {
      setSaving(true);
      setError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Unable to get authentication token');
      const payload = {
        brandingTitle: formData.brandingTitle,
        brandingDescription: formData.brandingDescription,
        logoUrl: formData.logoUrl,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        website: formData.website,
        seasonFeeDescription: formData.seasonFeeDescription,
        helpTitle: formData.helpTitle,
        helpContent: formData.helpContent,
        helpEmail: formData.helpEmail,
        supportedLevels: formData.supportedLevels,
        enableQualifiers: formData.enableQualifiers,
      };
      const response = await fetch(`${API_BASE_URL}/interclubs/${interclubId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }
      setSuccess('Settings saved successfully!');
      setEditingSection(null);
      await loadInterclub();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (interclub) {
      setFormData({
        brandingTitle: interclub.brandingTitle || '',
        brandingDescription: interclub.brandingDescription || '',
        logoUrl: interclub.logoUrl || '',
        contactEmail: interclub.contactEmail || interclub.contact?.email || '',
        contactPhone: interclub.contactPhone || interclub.contact?.phone || '',
        website: interclub.website || interclub.contact?.website || '',
        seasonFeeDescription: interclub.seasonFeeDescription || '',
        helpTitle: interclub.helpTitle || 'Need Help?',
        helpContent: interclub.helpContent || '',
        helpEmail: interclub.helpEmail || interclub.contact?.email || '',
        supportedLevels: interclub.supportedLevels || ALL_LEVEL_OPTIONS,
        enableQualifiers: interclub.enableQualifiers !== false,
      });
    }
    setEditingSection(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const EditSectionCard = ({ title, icon, children, section }) => (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, borderColor: 'primary.main', borderWidth: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.100', display: 'flex' }}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            <Chip label="Editing" size="small" color="primary" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<CloseIcon />} onClick={handleCancelEdit} sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => handleSave(section)} disabled={saving} sx={{ borderRadius: 2 }}>{saving ? 'Saving...' : 'Save'}</Button>
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex' }}>
            <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Series Settings</Typography>
            <Typography variant="body2" color="text.secondary">Branding, contact, fees, levels, and feature toggles for the series</Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Branding */}
        {editingSection === 'branding' ? (
          <EditSectionCard title="Branding" icon={<BusinessIcon color="primary" />} section="branding">
            <TextField fullWidth label="Branding Title" value={formData.brandingTitle} onChange={handleChange('brandingTitle')} placeholder="e.g., PNIC Interclub Competition" sx={{ mb: 2 }} helperText="Displayed as the main title on the home page" />
            <TextField fullWidth multiline rows={3} label="Branding Description" value={formData.brandingDescription} onChange={handleChange('brandingDescription')} placeholder="A brief description of your interclub..." sx={{ mb: 2 }} helperText="Displayed below the title on the home page" />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <TextField fullWidth label="Logo URL" value={formData.logoUrl} onChange={handleChange('logoUrl')} placeholder="https://example.com/logo.png" helperText="URL to your interclub logo image" />
              {formData.logoUrl && (
                <Box sx={{ width: 60, height: 60, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  <img src={formData.logoUrl} alt="Logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                </Box>
              )}
            </Box>
          </EditSectionCard>
        ) : (
          <SectionCard title="Branding" icon={<BusinessIcon color="primary" />} onEdit={() => setEditingSection('branding')}>
            <Grid container spacing={2}>
              <Grid item xs={12}><ViewField label="Branding Title" value={formData.brandingTitle} /></Grid>
              <Grid item xs={12}><ViewField label="Branding Description" value={formData.brandingDescription} /></Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Logo</Typography>
                {formData.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
                      <img src={formData.logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{formData.logoUrl.length > 60 ? `${formData.logoUrl.substring(0, 60)}...` : formData.logoUrl}</Typography>
                  </Box>
                ) : <Typography variant="body1" color="text.secondary">—</Typography>}
              </Grid>
            </Grid>
          </SectionCard>
        )}

        {/* Contact */}
        {editingSection === 'contact' ? (
          <EditSectionCard title="Contact Information" icon={<ContactIcon color="primary" />} section="contact">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth type="email" label="Contact Email" value={formData.contactEmail} onChange={handleChange('contactEmail')} placeholder="contact@yourinterclub.org" helperText="Primary contact email" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={formData.contactPhone} onChange={handleChange('contactPhone')} placeholder="(555) 123-4567" helperText="Contact phone (optional)" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Website" value={formData.website} onChange={handleChange('website')} placeholder="https://www.yourinterclub.org" helperText="Website URL" /></Grid>
            </Grid>
          </EditSectionCard>
        ) : (
          <SectionCard title="Contact Information" icon={<ContactIcon color="primary" />} onEdit={() => setEditingSection('contact')}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><ViewField label="Contact Email" value={formData.contactEmail} icon={<EmailIcon fontSize="small" />} /></Grid>
              <Grid item xs={12} sm={6}><ViewField label="Phone" value={formData.contactPhone} icon={<PhoneIcon fontSize="small" />} /></Grid>
              <Grid item xs={12}><ViewField label="Website" value={formData.website} icon={<WebsiteIcon fontSize="small" />} isLink /></Grid>
            </Grid>
          </SectionCard>
        )}

        {/* Season Cost */}
        {editingSection === 'seasonCost' ? (
          <EditSectionCard title="Season Cost Information" icon={<PaymentIcon color="primary" />} section="seasonCost">
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Describe what the season entry fee covers. This appears on the home page.</Alert>
            <TextField fullWidth multiline rows={4} label="Season Fee Description" value={formData.seasonFeeDescription} onChange={handleChange('seasonFeeDescription')} placeholder="The season entry fee registers your skater for the interclub championship series..." helperText="Explain what the entry fee is used for (leave empty for default)" />
          </EditSectionCard>
        ) : (
          <SectionCard title="Season Cost Information" icon={<PaymentIcon color="primary" />} onEdit={() => setEditingSection('seasonCost')}>
            <ViewField label="Season Fee Description" value={formData.seasonFeeDescription || 'Using default description'} />
          </SectionCard>
        )}

        {/* Supported Levels */}
        {editingSection === 'levels' ? (
          <EditSectionCard title="Supported Competition Levels" icon={<LevelIcon color="primary" />} section="levels">
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Select which skating levels your series supports. Only selected levels appear in registration dropdowns.</Alert>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}>Select All</Button>
              <Button size="small" variant="outlined" color="secondary" onClick={handleClearAll}>Clear All</Button>
              <Chip label={`${formData.supportedLevels?.length || 0} levels selected`} color="primary" size="small" sx={{ ml: 'auto' }} />
            </Box>
            {ALL_LEVELS.map((category) => {
              const allSelected = category.levels.every((level) => formData.supportedLevels?.includes(level));
              const someSelected = category.levels.some((level) => formData.supportedLevels?.includes(level));
              return (
                <Box key={category.category} sx={{ mb: 3 }}>
                  <FormControlLabel control={<Checkbox checked={allSelected} indeterminate={someSelected && !allSelected} onChange={() => handleCategoryToggle(category.levels)} color="primary" />} label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{category.category}</Typography>} />
                  <Box sx={{ ml: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {category.levels.map((level) => (
                      <Chip key={level} label={level} onClick={() => handleLevelToggle(level)} color={formData.supportedLevels?.includes(level) ? 'primary' : 'default'} variant={formData.supportedLevels?.includes(level) ? 'filled' : 'outlined'} sx={{ cursor: 'pointer' }} />
                    ))}
                  </Box>
                </Box>
              );
            })}
            {formData.supportedLevels?.length === 0 && <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>No levels selected. Users won't be able to select a level during registration.</Alert>}
          </EditSectionCard>
        ) : (
          <SectionCard title="Supported Competition Levels" icon={<LevelIcon color="primary" />} onEdit={() => setEditingSection('levels')}>
            <Box sx={{ mb: 2 }}><Chip label={`${formData.supportedLevels?.length || 0} of ${ALL_LEVEL_OPTIONS.length} levels enabled`} color="primary" size="small" /></Box>
            {ALL_LEVELS.map((category) => {
              const enabledInCategory = category.levels.filter((level) => formData.supportedLevels?.includes(level));
              if (enabledInCategory.length === 0) return null;
              return (
                <Box key={category.category} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{category.category}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {enabledInCategory.map((level) => <Chip key={level} label={level} size="small" color="primary" variant="outlined" icon={<CheckIcon sx={{ fontSize: 14 }} />} />)}
                  </Box>
                </Box>
              );
            })}
          </SectionCard>
        )}

        {/* Help */}
        {editingSection === 'help' ? (
          <EditSectionCard title="Help & Support" icon={<HelpIcon color="primary" />} section="help">
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Configure the help section on the home page. Users can click "Contact Us" to email your support address.</Alert>
            <TextField fullWidth label="Help Section Title" value={formData.helpTitle} onChange={handleChange('helpTitle')} placeholder="Need Help?" sx={{ mb: 2 }} helperText="Heading for the help section" />
            <TextField fullWidth multiline rows={3} label="Help Content" value={formData.helpContent} onChange={handleChange('helpContent')} placeholder="Have questions? We're here to help!" sx={{ mb: 2 }} helperText="Custom message (leave empty for default)" />
            <TextField fullWidth type="email" label="Help/Support Email" value={formData.helpEmail} onChange={handleChange('helpEmail')} placeholder="support@yourinterclub.org" helperText="Email for the Contact Us button. If empty, help section is hidden." />
          </EditSectionCard>
        ) : (
          <SectionCard title="Help & Support" icon={<HelpIcon color="primary" />} onEdit={() => setEditingSection('help')}>
            <Grid container spacing={2}>
              <Grid item xs={12}><ViewField label="Help Section Title" value={formData.helpTitle} /></Grid>
              <Grid item xs={12}><ViewField label="Help Content" value={formData.helpContent || 'Using default content'} /></Grid>
              <Grid item xs={12}><ViewField label="Help/Support Email" value={formData.helpEmail} icon={<EmailIcon fontSize="small" />} /></Grid>
            </Grid>
          </SectionCard>
        )}

        {/* Feature Toggles */}
        {editingSection === 'features' ? (
          <EditSectionCard title="Feature Toggles" icon={<FeatureIcon color="primary" />} section="features">
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Control which features are visible to users.</Alert>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={formData.enableQualifiers} onChange={(e) => setFormData((prev) => ({ ...prev, enableQualifiers: e.target.checked }))} color="primary" />}
                label={<Box><Typography variant="body1" sx={{ fontWeight: 500 }}>Show Qualifiers Page</Typography><Typography variant="body2" color="text.secondary">When enabled, the Qualifiers link appears in the navigation.</Typography></Box>}
                sx={{ alignItems: 'flex-start', mb: 2 }}
              />
            </FormGroup>
          </EditSectionCard>
        ) : (
          <SectionCard title="Feature Toggles" icon={<FeatureIcon color="primary" />} onEdit={() => setEditingSection('features')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={formData.enableQualifiers ? 'Qualifiers Enabled' : 'Qualifiers Disabled'} color={formData.enableQualifiers ? 'success' : 'default'} size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {formData.enableQualifiers ? 'The Qualifiers page is visible in the navigation.' : 'The Qualifiers page is hidden (admins can still access it).'}
            </Typography>
          </SectionCard>
        )}
      </Paper>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: 2 }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
}
